import React from "react"
import { Box, Center, Heading, Image, Text, VStack } from "@chakra-ui/react"
import { PageProps, graphql, navigate } from "gatsby"

import ResponsiveBlock from "~components/shared/layout/responsive-block"
import SEO from "~components/shared/SEO"
import TableOfContents from "./table-of-contents"
import MainContent from "./main-content"
import BlogFooter from "./footer"
import ChckvetLogo from "~images/logo/png/default-w-chckup.png"

const BlogPostTemplate: React.FC<PageProps<Queries.ContenfulBlogPostQuery>> = ({
    data: { contentfulBlogPost: blog, allContentfulBlogPost: allBlogs },
}) => {
    return (
        <>
            <SEO
                title={blog?.seoTitle || blog?.title || "Chckvet Blog Post"}
                description={
                    blog?.seoDescription ||
                    "Start saving time today with Chckvet."
                }
                imageUrl={blog?.featuredImage?.url || undefined}
            />
            <ResponsiveBlock pt="72px" minH="100dvh" display="flex">
                <TableOfContents
                    content={blog?.content?.raw || ""}
                    containerProps={{
                        display: { base: "none", md: "block" },
                    }}
                />
                <Box
                    minH="100%"
                    w="1px"
                    bg="gray.200"
                    my={8}
                    ml={6}
                    mr={8}
                    display={{ base: "none", md: "block" }}
                />
                <MainContent blog={blog} />
                <Center
                    flexDir="column"
                    mx={6}
                    display={{ base: "none", lg: "flex" }}
                    position="sticky"
                    top="72px"
                    h="fit-content"
                    overflowY="auto"
                    pr={0}
                    pb={8}
                    pt={4}
                    // mt={"600px"}
                    flexShrink={0}
                    maxW="200px"
                    minW="200px"
                    cursor="pointer"
                    onClick={() => {
                        navigate("/book-a-demo")
                    }}
                >
                    <VStack
                        borderWidth={1}
                        minH="530px"
                        maxH="530px"
                        minW="200px"
                        maxW="200px"
                        borderColor="blackAlpha.200"
                        rounded="xl"
                        mt={8}
                        p={4}
                        spacing={4}
                        align="center"
                        justify="center"
                        position="relative"
                        overflow="hidden"
                        boxShadow="sm"
                        transition="all 0.3s"
                        _hover={{
                            transform: "translateY(-2px)",
                            boxShadow: "md",
                        }}
                        _before={{
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgGradient:
                                "linear(to-b, white, blue.50, pink.50, purple.50)",
                            backgroundSize: "200% 200%",
                            animation: "gradientAnimation 8s ease infinite",
                            opacity: 0.7,
                            zIndex: -1,
                        }}
                        sx={{
                            "@keyframes gradientAnimation": {
                                "0%": { backgroundPosition: "0% 0%" },
                                "50%": { backgroundPosition: "100% 100%" },
                                "100%": { backgroundPosition: "0% 0%" },
                            },
                        }}
                    >
                        <Image
                            src={ChckvetLogo}
                            alt="Chckvet Logo"
                            width="120px"
                            mb={2}
                        />
                        <Heading
                            size="md"
                            textAlign="center"
                            color="blue.700"
                            mb={2}
                        >
                            Looking for a better solution?
                        </Heading>
                        <Text
                            fontSize="sm"
                            textAlign="center"
                            color="gray.600"
                            mb={4}
                        >
                            Discover how Chckvet can help fill your schedule{" "}
                            <b>
                                <i>and</i>
                            </b>{" "}
                            give your team their time back.
                        </Text>
                        <Box
                            as="button"
                            bg="blue.500"
                            color="white"
                            fontWeight="bold"
                            py={2}
                            px={4}
                            borderRadius="md"
                            _hover={{ bg: "blue.600" }}
                            mt={2}
                        >
                            Book a Demo
                        </Box>
                    </VStack>
                    {/* {blog?.bannerAds?.map(bannerAd =>
                        bannerAd ? (
                            <Box
                                borderWidth={1}
                                minH="530px"
                                maxH="530px"
                                minW="200px"
                                maxW="200px"
                                borderColor="blackAlpha.200"
                                mt={8}
                                key={bannerAd.url}
                            >
                                <Image
                                    objectFit={"cover"}
                                    w="full"
                                    h="full"
                                    src={bannerAd.url || ""}
                                />
                            </Box>
                        ) : null
                    )} */}
                </Center>
            </ResponsiveBlock>
            <BlogFooter allBlogs={allBlogs} />
        </>
    )
}

export const query = graphql`
    # $blogID comes from createPage() context in gatsby-node.js
    query ContenfulBlogPost($blogId: String!) {
        contentfulBlogPost(id: { eq: $blogId }) {
            id
            slug
            title
            shortDescription
            seoTitle
            seoDescription
            author {
                name
                avatar {
                    url
                }
            }
            content {
                raw
                references {
                    ... on ContentfulVideoLink {
                        contentful_id
                        __typename
                        id
                        url
                    }
                    ... on ContentfulAsset {
                        contentful_id
                        __typename
                        id
                        title
                        description
                        file {
                            contentType
                            fileName
                            url
                        }
                    }
                }
            }
            featuredImage {
                url
            }
            bannerAds {
                url
            }
            publicationDate
        }
        allContentfulBlogPost(limit: 10, sort: { publicationDate: DESC }) {
            nodes {
                id
                title
                slug
                shortDescription
                publicationDate
                featuredImage {
                    url
                }
            }
        }
    }
`

export default BlogPostTemplate
